import React, { useCallback, useEffect, useState } from "react";
import Modal from "./Modal";
import { Plus } from "lucide-react";
import { btn } from "../utils/buttonStyles";
import { useApi } from "../hooks/useApi";
import { formatUtcDateTime } from "../utils/dateTime";
import { deviceDeepLink } from "./DeviceInventoryPanel";
import { CyberAssetForm, EspForm, RetireForm, SystemForm } from "./NercAssetForms";
import { IMPACT_TONES, apiError } from "../utils/nercAssets";

/**
 * One site, opened under its row in the NERC sites list: its Electronic Security Perimeters, BES
 * Cyber Systems and Cyber Assets, each with add / edit / retire for managers, the manual Defender
 * device link on a Cyber Asset, and the site's activity history. Nothing here scores.
 */

const ACTION_LABELS = {
  Created: "created", Updated: "updated", Retired: "retired", Imported: "imported from CSV",
  DeviceLinked: "linked a Defender device", DeviceUnlinked: "unlinked the Defender device",
};
const ENTITY_LABELS = {
  Site: "Site", Esp: "ESP", BesCyberSystem: "BES Cyber System", CyberAsset: "Cyber Asset", SeniorManager: "CIP Senior Manager record",
  // Patch management entries recorded against a Cyber Asset (CIP-007 R2).
  PatchSource: "Patch source", NoPatchSource: "\"No patch source\" statement", PatchEvaluation: "Patch evaluation",
  Patch: "Patch", PatchAssetState: "Patch decision", PatchPlan: "Mitigation plan", PatchSettings: "Patch management settings",
};

const nameOf = (json) => {
  try { const o = JSON.parse(json || "null"); return o?.Name || o?.Asset?.Name || null; } catch { return null; }
};

const ImpactChip = ({ impact }) => impact
  ? <span className={`px-1.5 py-0.5 rounded border text-[11px] font-semibold ${IMPACT_TONES[impact] || ""}`}>{impact}</span>
  : <span className="text-[11px] text-gray-400">Not categorised</span>;

function Section({ title, count, onAdd, canManage, addLabel, children, testId }) {
  return (
    <section className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800">{title} <span className="text-gray-400 font-normal">({count})</span></h4>
        {canManage && onAdd && (
          <button type="button" onClick={onAdd} className={btn("secondary", "xs")}><Plus className="h-3.5 w-3.5" />{addLabel}</button>
        )}
      </div>
      {children}
    </section>
  );
}

const RowActions = ({ canManage, retired, onEdit, onRetire, extra }) => !canManage || retired ? null : (
  <span className="flex gap-2 justify-end whitespace-nowrap">
    {extra}
    <button type="button" className={btn("secondary", "xs")} onClick={onEdit}>Edit</button>
    <button type="button" className={btn("danger", "xs")} onClick={onRetire}>Retire</button>
  </span>
);

function DevicePicker({ asset, onClose, onLinked }) {
  const apiCall = useApi();
  const [search, setSearch] = useState(asset.hostname || asset.name || "");
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const find = useCallback(async (term) => {
    setError(null);
    try { setResults(await apiCall("GET", `/nerc-assets/devices?search=${encodeURIComponent(term || "")}`)); }
    catch (err) { setError(apiError(err, "Could not search the Devices inventory.")); }
  }, [apiCall]);

  useEffect(() => { find(search); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const link = async (deviceId) => {
    setError(null);
    try { await apiCall("PUT", `/nerc-assets/cyber-assets/${asset.id}/device`, { deviceAssetId: deviceId }); onLinked(); }
    catch (err) { setError(apiError(err, "Could not link the device.")); }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Link "${asset.name}" to a Defender device`} size="2xl">
      <div className="space-y-3" data-testid="nerc-device-picker">
        <p className="text-sm text-gray-600">Pick the device yourself: Axaris never matches OT assets to devices automatically. A device can be linked to one Cyber Asset only.</p>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); find(search); }}>
          <input className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, DNS name or IP" />
          <button type="submit" className={btn("primary", "md")}>Search</button>
        </form>
        {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
        {results && results.length === 0 && <p className="text-sm text-gray-500">No device in the Devices inventory matches.</p>}
        {results && results.length > 0 && (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500"><th className="py-1">Device</th><th>OS</th><th>IP</th><th>Health</th><th /></tr></thead>
            <tbody>
              {results.map((d) => (
                <tr key={d.id} className="border-t border-gray-100">
                  <td className="py-1.5">{d.displayName || d.dnsName}</td>
                  <td>{d.osPlatform || "—"}</td>
                  <td>{d.lastIpAddress || "—"}</td>
                  <td>{d.healthStatus || "—"}</td>
                  <td className="text-right">
                    {d.linkedCyberAssetId && d.linkedCyberAssetId !== asset.id
                      ? <span className="text-xs text-gray-400">Linked to another Cyber Asset</span>
                      : <button type="button" className={btn("primary", "xs")} onClick={() => link(d.id)}>Link</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}

export default function NercSiteDetails({ siteId, canManage, includeRetired, users, onChanged }) {
  const apiCall = useApi();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);   // { kind, item }
  const [unlinkError, setUnlinkError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try { setDetail(await apiCall("GET", `/nerc-assets/sites/${siteId}?includeRetired=${includeRetired ? "true" : "false"}`)); }
    catch (err) { setError(apiError(err, "Could not load this site.")); }
  }, [apiCall, siteId, includeRetired]);

  useEffect(() => { load(); }, [load]);

  const saved = () => { setDialog(null); load(); onChanged?.(); };

  const unlink = async (asset) => {
    setUnlinkError(null);
    try { await apiCall("DELETE", `/nerc-assets/cyber-assets/${asset.id}/device`); saved(); }
    catch (err) { setUnlinkError(apiError(err, "Could not unlink the device.")); }
  };

  if (error) return <div className="p-4 text-sm text-red-700" role="alert">{error}</div>;
  if (!detail) return <div className="p-4 text-sm text-gray-500">Loading…</div>;

  const { esps, systems, cyberAssets, activity } = detail;
  const retiredTag = (r) => r.retiredAt && <span className="ml-1 text-[10px] uppercase text-gray-400" title={r.retiredReason || ""}>retired</span>;

  return (
    <div className="p-4 space-y-5 bg-gray-50 border-t border-gray-200" data-testid="nerc-site-details">
      <Section title="BES Cyber Systems" count={systems.length} canManage={canManage} addLabel="Add system"
        onAdd={() => setDialog({ kind: "system" })} testId="nerc-systems">
        {systems.length === 0 ? <p className="text-sm text-gray-500">No BES Cyber Systems yet. The site stays "Not categorised" until one is added.</p> : (
          <table className="w-full text-sm bg-white rounded-lg border border-gray-200">
            <thead><tr className="text-left text-xs text-gray-500"><th className="px-3 py-2">Name</th><th>Impact</th><th>Criterion</th><th>Owner</th><th>Cyber Assets</th><th /></tr></thead>
            <tbody>
              {systems.map((s) => (
                <tr key={s.id} className={`border-t border-gray-100 ${s.retiredAt ? "opacity-60" : ""}`}>
                  <td className="px-3 py-2">{s.name}{retiredTag(s)}</td>
                  <td><ImpactChip impact={s.impact} /></td>
                  <td>{s.criterion}</td>
                  <td>{s.ownerName || "—"}</td>
                  <td>{s.cyberAssets}</td>
                  <td className="px-3"><RowActions canManage={canManage} retired={s.retiredAt} onEdit={() => setDialog({ kind: "system", item: s })} onRetire={() => setDialog({ kind: "retire-system", item: s })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Electronic Security Perimeters" count={esps.length} canManage={canManage} addLabel="Add ESP"
        onAdd={() => setDialog({ kind: "esp" })} testId="nerc-esps">
        {esps.length === 0 ? <p className="text-sm text-gray-500">No Electronic Security Perimeters. High and Medium impact Cyber Assets with routable connectivity need one.</p> : (
          <table className="w-full text-sm bg-white rounded-lg border border-gray-200">
            <thead><tr className="text-left text-xs text-gray-500"><th className="px-3 py-2">Name</th><th>Description</th><th>Cyber Assets</th><th /></tr></thead>
            <tbody>
              {esps.map((e) => (
                <tr key={e.id} className={`border-t border-gray-100 ${e.retiredAt ? "opacity-60" : ""}`}>
                  <td className="px-3 py-2">{e.name}{retiredTag(e)}</td>
                  <td>{e.description || "—"}</td>
                  <td>{e.cyberAssets}</td>
                  <td className="px-3"><RowActions canManage={canManage} retired={e.retiredAt} onEdit={() => setDialog({ kind: "esp", item: e })} onRetire={() => setDialog({ kind: "retire-esp", item: e })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Cyber Assets" count={cyberAssets.length} canManage={canManage && systems.some((s) => !s.retiredAt)} addLabel="Add Cyber Asset"
        onAdd={() => setDialog({ kind: "asset" })} testId="nerc-assets">
        {unlinkError && <p className="text-sm text-red-700" role="alert">{unlinkError}</p>}
        {cyberAssets.length === 0 ? <p className="text-sm text-gray-500">No Cyber Assets. Low impact systems do not need a Cyber Asset list (CIP-002 R1.3).</p> : (
          <table className="w-full text-sm bg-white rounded-lg border border-gray-200">
            <thead><tr className="text-left text-xs text-gray-500"><th className="px-3 py-2">Name</th><th>Class</th><th>Systems</th><th>ESP</th><th>IP</th><th>Defender device</th><th /></tr></thead>
            <tbody>
              {cyberAssets.map((a) => (
                <tr key={a.id} className={`border-t border-gray-100 align-top ${a.retiredAt ? "opacity-60" : ""}`} data-testid={`nerc-asset-${a.id}`}>
                  <td className="px-3 py-2">
                    {a.name}{retiredTag(a)}
                    {a.needsEsp && <span className="ml-2 px-1.5 py-0.5 rounded border text-[10px] font-semibold bg-red-50 text-red-700 border-red-200"
                      title="Routable connectivity on a High or Medium impact asset with no Electronic Security Perimeter (CIP-005 R1.1)">Needs ESP</span>}
                    {a.source === "Csv" && <span className="ml-1 text-[10px] text-gray-400">CSV</span>}
                  </td>
                  <td>{a.classification}</td>
                  <td>{a.systemNames.join(", ")}</td>
                  <td>{a.espName || "—"}</td>
                  <td>{a.ipAddress || "—"}</td>
                  <td>
                    {a.linkedDevice ? (
                      <span className="space-y-0.5 block">
                        <a className="text-indigo-600 hover:underline" href={deviceDeepLink(a.linkedDevice.id)}>{a.linkedDevice.displayName || a.linkedDevice.dnsName}</a>
                        <span className="block text-[11px] text-gray-500">
                          {a.linkedDevice.healthStatus || "Unknown health"}
                          {a.linkedDevice.riskScore ? ` · Defender alert risk ${a.linkedDevice.riskScore}` : ""}
                          {!a.linkedDevice.seenInLastSync ? " · not seen in the last sync" : ""}
                        </span>
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3">
                    <RowActions canManage={canManage} retired={a.retiredAt}
                      onEdit={() => setDialog({ kind: "asset", item: a })}
                      onRetire={() => setDialog({ kind: "retire-asset", item: a })}
                      extra={a.linkedDevice
                        ? <button type="button" className={btn("neutral", "xs")} onClick={() => unlink(a)}>Unlink device</button>
                        : <button type="button" className={btn("neutral", "xs")} onClick={() => setDialog({ kind: "device", item: a })}>Link device</button>} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <section className="space-y-2" data-testid="nerc-activity">
        <h4 className="text-sm font-semibold text-gray-800">Activity</h4>
        {activity.length === 0 ? <p className="text-sm text-gray-500">No changes recorded.</p> : (
          <ul className="text-sm space-y-1 max-h-60 overflow-y-auto">
            {activity.map((l) => (
              <li key={l.id} className="text-gray-700">
                <span className="text-gray-500">{formatUtcDateTime(l.at)}</span>{" · "}
                <span className="font-medium">{l.actorName || "Someone"}</span>{" "}
                {ACTION_LABELS[l.action] || l.action}{" "}
                {ENTITY_LABELS[l.entityType] || l.entityType}{" "}
                <span className="font-medium">{nameOf(l.afterJson) || nameOf(l.beforeJson) || ""}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {dialog?.kind === "system" && <SystemForm siteId={siteId} system={dialog.item} users={users} onClose={() => setDialog(null)} onSaved={saved} />}
      {dialog?.kind === "esp" && <EspForm siteId={siteId} esp={dialog.item} onClose={() => setDialog(null)} onSaved={saved} />}
      {dialog?.kind === "asset" && <CyberAssetForm siteId={siteId} asset={dialog.item} systems={systems} esps={esps} onClose={() => setDialog(null)} onSaved={saved} />}
      {dialog?.kind === "device" && <DevicePicker asset={dialog.item} onClose={() => setDialog(null)} onLinked={saved} />}
      {dialog?.kind === "retire-system" && <RetireForm kind="system" item={dialog.item} onClose={() => setDialog(null)} onSaved={saved} />}
      {dialog?.kind === "retire-esp" && <RetireForm kind="esp" item={dialog.item} onClose={() => setDialog(null)} onSaved={saved} />}
      {dialog?.kind === "retire-asset" && <RetireForm kind="asset" item={dialog.item} onClose={() => setDialog(null)} onSaved={saved} />}
    </div>
  );
}
